import { KeyRoundIcon, TriangleAlertIcon } from "lucide-react";
import { FullPage, PulseLogo } from "@/components/full-page";
import { ENV_KEYS, type EnvKey } from "@/lib/env";

const HELP: Record<EnvKey, string> = {
  [ENV_KEYS.tenantId]: "Directory (tenant) ID of the Datanomy Entra tenant.",
  [ENV_KEYS.clientId]: "Application (client) ID of the “Pulse Web (dev)” SPA app registration.",
  [ENV_KEYS.apiScope]: "The delegated scope exposed by “Pulse API (dev)”, e.g. api://<api-client-id>/access_as_user.",
};

/** Shown instead of crashing when the Entra settings aren't configured. */
export function SetupScreen({ missing, error }: { missing: EnvKey[]; error?: string }) {
  return (
    <FullPage className="max-w-xl">
      <div className="rounded-xl border bg-card p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-3">
          <PulseLogo />
          <div>
            <h1 className="text-base font-semibold">Finish setting up Pulse</h1>
            <p className="text-[13px] text-muted-foreground">Sign-in needs a few Entra ID values.</p>
          </div>
        </div>

        {error && (
          <div className="mb-5 flex gap-2 rounded-lg border border-destructive/30 bg-destructive/8 p-3 text-[13px] text-destructive">
            <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <p className="mb-4 text-sm text-muted-foreground">
          Create <code className="rounded bg-muted px-1 py-0.5 font-mono text-[12px] text-foreground">pulse-app/web/.env.local</code>{" "}
          (copy <code className="rounded bg-muted px-1 py-0.5 font-mono text-[12px] text-foreground">.env.example</code>) and set:
        </p>

        <ul className="mb-6 space-y-3">
          {(Object.values(ENV_KEYS) as EnvKey[]).map((key) => {
            const isMissing = missing.includes(key);
            return (
              <li key={key} className="flex gap-3">
                <KeyRoundIcon
                  className={isMissing ? "mt-0.5 size-4 shrink-0 text-warning" : "mt-0.5 size-4 shrink-0 text-success"}
                />
                <div>
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-[13px] font-medium">{key}</code>
                    <span className={isMissing ? "text-xs text-warning" : "text-xs text-success"}>
                      {isMissing ? "missing" : "set"}
                    </span>
                  </div>
                  <p className="text-[13px] text-muted-foreground">{HELP[key]}</p>
                </div>
              </li>
            );
          })}
        </ul>

        <pre className="overflow-x-auto rounded-lg border bg-muted/60 p-4 font-mono text-[12px] leading-relaxed">
{`VITE_ENTRA_TENANT_ID=<tenant-id>
VITE_ENTRA_WEB_CLIENT_ID=<pulse-web-client-id>
VITE_ENTRA_API_SCOPE=api://<pulse-api-client-id>/access_as_user
VITE_API_BASE_URL=/api`}
        </pre>

        <p className="mt-4 text-[13px] text-muted-foreground">
          Then restart <code className="font-mono text-[12px]">pnpm dev</code>. None of these values are secrets. The
          SPA redirect URI must be <code className="font-mono text-[12px]">{window.location.origin}</code>.
        </p>
      </div>
    </FullPage>
  );
}
